import React, { useContext, useState } from 'react';

import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import ErrorMessage from 'common/ErrorMessage';
import ModernModal from 'common/modals/ModernModal';
import { useCreateIdeaFromInsight } from 'common/subdomain/admin/v2/hooks/useIdeaMutations';
import { useQueryParams } from 'common/subdomain/admin/v2/hooks/useQueryParams';
import HStack from 'common/ui/HStack';
import Button from 'common/uiV3/Button';
import Field from 'common/uiV3/Field';
import Input from 'common/uiV3/Input';

import type { Insight } from 'common/api/endpoints/insight';
import type { View } from 'common/api/endpoints/view';

type OwnProps = {
  onClose: () => void;
  onSuccess: () => void;
  insight: Insight;
  selectedView?: View | null;
};

type Props = OwnProps;

function CreateIdeaFromInsightModal({ onClose, onSuccess, insight, selectedView }: Props) {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);
  const showToast = useContext(ShowToastContext);

  const [title, setTitle] = useState('');

  const ideaQueryParams = useQueryParams(selectedView);

  const createIdeaFromInsight = useCreateIdeaFromInsight(ideaQueryParams, {
    onSuccess: (createdIdea) => {
      showToast('Idea created', ToastTypes.success);
      onSuccess();
      onClose();

      router.push({
        pathname: location.pathname,
        query: {
          ...location.query,
          idea: createdIdea.idea.urlName,
        },
      });
    },
  });

  const onCreate = async () => {
    if (!title.trim()) {
      return;
    }

    await createIdeaFromInsight.mutate({
      title: title.trim(),
      insightID: insight._id,
    });
  };

  return (
    <ModernModal
      width={420}
      footer={
        <CreateIdeaFromInsightModalFooter
          disabled={!title.trim()}
          loading={createIdeaFromInsight.isLoading}
          onClose={onClose}
          onCreate={onCreate}
        />
      }
      header="Create Idea"
      onClose={onClose}
      sections={[
        <CreateIdeaFromInsightModalBody
          key="body"
          error={!!createIdeaFromInsight.error}
          title={title}
          onTitleChange={setTitle}
          onCreate={onCreate}
        />,
      ]}
    />
  );
}

type BodyProps = {
  error: boolean;
  title: string;
  onTitleChange: (title: string) => void;
  onCreate: () => void;
};

function CreateIdeaFromInsightModalBody({ error, title, onTitleChange, onCreate }: BodyProps) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onCreate();
      }}
      className="flex flex-col gap-4">
      <p className="text-muted-foreground">
        This action will create a new idea and move this insight over.
      </p>
      <Field>
        <Field.Label required>Idea title</Field.Label>
        <Field.Content>
          <Input onChange={onTitleChange} placeholder="Enter idea title" value={title} autoFocus />
        </Field.Content>
      </Field>
      {error ? <ErrorMessage>Something went wrong, please try again later.</ErrorMessage> : null}
    </form>
  );
}

type FooterProps = {
  disabled: boolean;
  loading: boolean;
  onClose: () => void;
  onCreate: () => void;
};

function CreateIdeaFromInsightModalFooter({ disabled, loading, onClose, onCreate }: FooterProps) {
  return (
    <HStack className="justify-end gap-2">
      <Button onClick={onClose} variant="outlined">
        Cancel
      </Button>
      <Button disabled={disabled} isLoading={loading} onClick={onCreate} variant="contained">
        Create
      </Button>
    </HStack>
  );
}

export default CreateIdeaFromInsightModal;
